import { supabase } from "@/integrations/supabase/client";
import { carryOverDescription, readCarryOver } from "@/lib/onboarding/carryOver";
import { createAmpPlan } from "@/lib/amp/createPlan";
import { generateGuaranteedUniqueAssessmentId } from "@/utils/assessmentUtils";
import type { RiskPhase } from "@/lib/riskPhase";

export type StandaloneDocId =
  | "amp"
  | "risk-utforande"
  | "risk-bruksskede"
  | "arbetsomrade"
  | "arbetsberedning"
  | "skr"
  | "projektorsleverans";

export interface StandaloneDocDef {
  id: StandaloneDocId;
  title: string;
  docNumber: string;
  /** Vad dokumentet är, i en mening. */
  what: string;
  /** När du behöver det. */
  when: string;
  /** Ungefärlig tid att komma igång. */
  time: string;
  /** Ikonnamn från lucide-react (mappas i UI). */
  icon: "file" | "shield" | "map" | "hardhat" | "gauge" | "package";
  /** Föreslaget namn på projektet/dokumentet. */
  defaultName: string;
}

export const STANDALONE_DOCS: StandaloneDocDef[] = [
  {
    id: "amp",
    title: "Arbetsmiljöplan (AMP)",
    docNumber: "Dok 12",
    what: "Projektets huvuddokument med rubriker enligt AFS 2023:3 och förifyllning från dina underlag.",
    when: "Krävs innan byggarbetsplatsen etableras när särskilda risker finns eller förhandsanmälan krävs.",
    time: "ca 20 min",
    icon: "file",
    defaultName: "Arbetsmiljöplan",
  },
  {
    id: "risk-utforande",
    title: "Risksammanställning – utförande",
    docNumber: "Dok 30b",
    what: "Risker under byggskedet: arbetsmoment, etablering och produktion.",
    when: "Under projektering och inför byggstart, som underlag till AMP och arbetsberedningar.",
    time: "ca 30 min",
    icon: "shield",
    defaultName: "Risksammanställning utförande",
  },
  {
    id: "risk-bruksskede",
    title: "Risksammanställning – bruksskede",
    docNumber: "Dok 31",
    what: "Kvarstående risker vid drift, underhåll och användning av byggnadsverket.",
    when: "Inför överlämning till förvaltning eller när projektörsansvaret ska dokumenteras.",
    time: "ca 20 min",
    icon: "shield",
    defaultName: "Risksammanställning bruksskede",
  },
  {
    id: "arbetsomrade",
    title: "Arbetsområdesutredning",
    docNumber: "Dok 10",
    what: "Genomgång av arbetsområdets förutsättningar med ja/nej/ej aktuellt och motivering.",
    when: "Tidigt i projekteringen, innan riskerna sammanställs.",
    time: "ca 15 min",
    icon: "map",
    defaultName: "Arbetsområdesutredning",
  },
  {
    id: "arbetsberedning",
    title: "Arbetsberedning",
    docNumber: "Dok 23",
    what: "AI-stödd beredning av ett arbetsmoment med risker, skyddsåtgärder och kompetenskrav.",
    when: "Före riskfyllda moment – fungerar även fristående mot projekt som inte använder systemet.",
    time: "ca 10 min",
    icon: "hardhat",
    defaultName: "Arbetsberedning",
  },
  {
    id: "skr",
    title: "SKR-bedömning",
    docNumber: "Dok 05",
    what: "Bedömning av projektets storlek, komplexitet och risknivå.",
    when: "I starten, för att avgöra omfattningen på arbetsmiljöarbetet.",
    time: "ca 10 min",
    icon: "gauge",
    defaultName: "SKR-bedömning",
  },
  {
    id: "projektorsleverans",
    title: "Projektörsleverans",
    docNumber: "Dok 18/19",
    what: "Rutin och leveransintyg som visar hur projektörsansvaret uppfylls, med verifieringskod.",
    when: "När du som projektör ska lämna in underlag till ett projekt utanför systemet.",
    time: "ca 15 min",
    icon: "package",
    defaultName: "Projektörsleverans",
  },
];

export const getStandaloneDoc = (id: string): StandaloneDocDef | undefined =>
  STANDALONE_DOCS.find((d) => d.id === id);

/** Hämtar eller skapar standardmappen "Allmänt" i ett projekt. */
export async function ensureDefaultFolder(projectId: string, userId: string): Promise<string | null> {
  const { data: existing } = await supabase
    .from("folders")
    .select("id, name, parent_folder_id")
    .eq("project_id", projectId)
    .is("parent_folder_id", null);

  const found = (existing || []).find((f: any) => f?.name?.toLowerCase() === "allmänt");
  if (found?.id) return found.id as string;

  const { data, error } = await supabase
    .from("folders")
    .insert({
      name: "Allmänt",
      description: null,
      project_id: projectId,
      parent_folder_id: null,
      created_by: userId,
    })
    .select("id")
    .single();

  if (error || !data) return null;
  return data.id as string;
}

async function createRiskAssessment(
  projectId: string,
  folderId: string,
  name: string,
  phase: RiskPhase,
  userId: string
): Promise<string | null> {
  const assessmentId = await generateGuaranteedUniqueAssessmentId(projectId, folderId);

  const data = {
    status: "utkast",
    svar: {},
    projektNamn: name,
    assessmentName: name,
    projektbeskrivning: "",
    assessmentId,
    phase,
    metadata: {
      version: "1.0",
      created: new Date().toISOString(),
      projectId,
      folderId,
      phase,
      createdBy: userId,
    },
  };

  const { error } = await supabase.from("folder_risk_assessments").insert({
    folder_id: folderId,
    project_id: projectId,
    name,
    description: "",
    assessment_id: assessmentId,
    type: "riskbedomning",
    phase,
    data,
    created_by: userId,
    updated_by: userId,
  });

  if (error) return null;
  return assessmentId;
}

export interface StartStandaloneResult {
  projectId: string;
  route: string;
}

/**
 * Skapar ett enskilt dokument: projekt i standalone-läge, standardmapp
 * och själva dokumentet. Returnerar rutten där dokumentet öppnas.
 */
export async function startStandaloneDocument(
  docId: StandaloneDocId,
  name: string,
  userId: string
): Promise<StartStandaloneResult> {
  const doc = getStandaloneDoc(docId);
  const cleanName = (name || "").trim() || doc?.defaultName || "Nytt dokument";

  // Uppgifter från tidigare steg följer automatiskt med det nya dokumentet.
  const carried = carryOverDescription(readCarryOver());
  const baseDescription = `${doc?.title ?? "Dokument"} skapat ${new Date().toLocaleDateString("sv-SE")}`;

  const { data: project, error: projectError } = await supabase
    .from("projects")
    .insert({
      name: cleanName,
      user_id: userId,
      description: carried ? `${baseDescription} · ${carried}` : baseDescription,
      mode: "standalone",
      primary_document: docId,
    })
    .select("id")
    .single();

  if (projectError || !project) {
    throw new Error(projectError?.message || "Kunde inte skapa dokumentet");
  }

  const projectId = project.id as string;
  const folderId = await ensureDefaultFolder(projectId, userId);

  switch (docId) {
    case "amp": {
      const { planId } = await createAmpPlan({ projectId, folderId, name: cleanName, userId });
      if (!planId) throw new Error("Kunde inte skapa arbetsmiljöplanen");
      return { projectId, route: `/amp/${projectId}/${planId}` };
    }
    case "risk-utforande":
    case "risk-bruksskede": {
      if (!folderId) throw new Error("Kunde inte skapa standardmappen");
      const phase: RiskPhase = docId === "risk-bruksskede" ? "bruksskede" : "byggskede";
      const assessmentId = await createRiskAssessment(projectId, folderId, cleanName, phase, userId);
      if (!assessmentId) throw new Error("Kunde inte skapa risksammanställningen");
      return {
        projectId,
        route: `/riskbedomning?projektId=${projectId}&folderId=${folderId}&assessmentId=${assessmentId}&steg=1`,
      };
    }
    case "arbetsomrade":
      return { projectId, route: `/arbetsomrade/${projectId}` };
    case "arbetsberedning": {
      if (!folderId) throw new Error("Kunde inte skapa standardmappen");
      return { projectId, route: `/arbetsberedning/${projectId}/${folderId}` };
    }
    case "skr": {
      if (!folderId) throw new Error("Kunde inte skapa standardmappen");
      return { projectId, route: `/skr-bedomning/${projectId}/${folderId}` };
    }
    case "projektorsleverans":
      return { projectId, route: `/uppdrag/${projectId}?tools=basp` };
    default:
      return { projectId, route: `/uppdrag/${projectId}` };
  }
}

export interface RecentStandaloneDoc {
  projectId: string;
  docId: StandaloneDocId;
  title: string;
  name: string;
  updatedAt: string;
}

/** Hämtar användarens senaste enskilda dokument. */
export async function loadRecentStandaloneDocs(userId: string): Promise<RecentStandaloneDoc[]> {
  const { data, error } = await supabase
    .from("projects")
    .select("id, name, primary_document, updated_at, created_at")
    .eq("user_id", userId)
    .eq("mode", "standalone")
    .order("updated_at", { ascending: false })
    .limit(12);

  if (error || !data) return [];

  return data
    .filter((p: any) => !!p.primary_document)
    .map((p: any) => {
      const def = getStandaloneDoc(p.primary_document);
      return {
        projectId: p.id,
        docId: p.primary_document as StandaloneDocId,
        title: def?.title ?? "Dokument",
        name: p.name,
        updatedAt: p.updated_at || p.created_at,
      };
    });
}
